import React, { useState } from 'react'
import T from 'prop-types'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import { Box, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import InputLabel from 'components/atoms/InputLabel'

export default function MessageDialog({
  messages = [],
  openConfirmationDialog,
  handleCloseConfirmationDialog,
  handleConfirmConfirmationDialog,
  readOnly,
}) {
  const { t } = useTranslation()

  const [localTextareaValue, setLocalTextareaValue] = useState('')

  const handleReset = () => {
    if (localTextareaValue !== '') {
      setLocalTextareaValue('')
    }
  }

  const handleChangeTextareaLocal = (event) => {
    setLocalTextareaValue(event.target.value)
  }

  const handleClose = () => {
    handleCloseConfirmationDialog()
    handleReset()
  }

  const handleConfirm = () => {
    handleConfirmConfirmationDialog(localTextareaValue)
    handleReset()
  }

  return (
    <ConfirmationDialog
      title={t('carsToBuy.messages.messageDialog.title')}
      content={
        <>
          {!!messages.length && (
            <>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="textPrimary">
                  {t(
                    `carsToBuy.messages.messageDialog.comment${
                      messages.length > 1 ? 's' : ''
                    }Left`,
                    { number: messages.length }
                  )}
                </Typography>
                <Stack direction="row">
                  <Typography variant="body2" color="textPrimary">
                    {t('common.form.sessionId')}
                  </Typography>
                  <Box pr={0.5} />
                  <Typography variant="body2" color="textSecondary">
                    {messages[0]?.sessionId}
                  </Typography>
                </Stack>
              </Stack>
              <Box sx={{ maxHeight: '35vh', overflow: 'auto' }}>
                {messages.map((message) => (
                  <Box key={message.id} pt={1}>
                    <Typography
                      component="p"
                      variant="caption"
                      color="textSecondary"
                      align="right"
                    >
                      {format(
                        new Date(message.createdAt),
                        t('common.date.format'),
                        {
                          locale: itLocale,
                        }
                      )}
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                      {message.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box pb={4} />
            </>
          )}

          <InputLabel label={t('common.form.message')} />
          <TextField
            placeholder={t('common.form.messagePlaceholder')}
            value={localTextareaValue}
            onChange={handleChangeTextareaLocal}
            multiline
            fullWidth
            rows={2}
            rowsmax={4}
            disabled={readOnly}
          />
        </>
      }
      contentComponent={Box}
      open={openConfirmationDialog}
      readOnly={!localTextareaValue.trim() || readOnly}
      handleClose={handleClose}
      handleSubmit={handleConfirm}
    />
  )
}

MessageDialog.propTypes = {
  messages: T.array,
  openConfirmationDialog: T.bool,
  handleCloseConfirmationDialog: T.func,
  handleConfirmConfirmationDialog: T.func,
  readOnly: T.bool,
}
